import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Web3 from 'web3';
import detectEthereumProvider from '@metamask/detect-provider';
import '../style/employee.css'; // Import the CSS file
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const contractDOC = require("./EmployeeDocumentManagement.json");
const DOCABI = contractDOC.abi;
const AdvancedEmployeeDocumentManagementADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;

const Employee = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { walletAddress } = location.state || {}; // Retrieve wallet address from state
    const [employeeDataList, setEmployeeDataList] = useState([]);
    const [employeename, setEmployeename] = useState([]);
    const [loading, setLoading] = useState(true); // Added loading state
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedYear, setSelectedYear] = useState(''); // State for selected year
    const [selectedBranch, setSelectedBranch] = useState(''); // State for selected branch
    const [selectedDocumentType, setSelectedDocumentType] = useState(''); // State for selected document type

    useEffect(() => {
        const fetchEmployeeData = async () => {
            setLoading(true); // Set loading to true before starting fetch
            const provider = await detectEthereumProvider();
            if (provider) {
                const web3 = new Web3(provider);
                const networkId = await web3.eth.net.getId();
                if (Number(networkId) !== 80002) { // 80002 is the network ID for Polygon Mumbai testnet
                    toast.error('You are not connected to the correct network. Please switch to the Polygon Amoy testnet.');
                    return; // Stop execution if network is incorrect
                }
                const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
                try {
                    const DATA = [];
                    const ids = await contract.methods.getEmployeeDocuments(walletAddress).call();
                    const info = await contract.methods.getEmployeeInfo(walletAddress).call();
                    setEmployeename(info.name);

                    // Fetch additional employee details
                    const employeeInfo = await contract.methods.getEmployeeInfo(walletAddress).call();                    for (let index = 0; index < ids.length; index++) {
                        const employeeData = await contract.methods.getDocument(ids[index]).call();
                        DATA.push({
                            employeeID: employeeData.id,
                            addedAt: employeeData[4],
                            documentType: employeeData.documentType,
                            documentHash: employeeData.documentHash,
                            verificationStatus: employeeData.isApproved,
                            verifiedBy: employeeData.approvedBy,
                            approvedAt: employeeData.approvedAt,
                            document: `${process.env.REACT_APP_PINATA_URL}${employeeData.documentHash}`,
                            // Add the additional details
                            name: employeeInfo.name,
                            year: employeeInfo.department,
                            // branch: employeeInfo.branch,
                            idnumber: employeeInfo.idNumber
                        });
                    }
                    setEmployeeDataList(DATA);
                } catch (error) {
                    console.error('Error fetching employee data:', error);
                    toast.error('Error fetching employee data.');
                } finally {
                    setLoading(false); // Set loading to false after fetch completes
                }
            } else {
                toast.error('MetaMask is not installed. Please install MetaMask and try again.');
                navigate('/');
            }
        };

        const checkUserRole = async () => {
            const provider = await detectEthereumProvider();
            if (provider) {
                const web3 = new Web3(provider);
                const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
                try {
                    const accounts = await provider.request({ method: 'eth_requestAccounts' });
                    const walletAddress = accounts[0]; // Get the first account address

                    // Define role constants based on role
                    const role = 'employee'; // This should be dynamically set based on your logic
                    let roleConstant;
                    switch (role) {
                        case 'employee':
                            roleConstant = await contract.methods.EMPLOYEE_ROLE().call();
                            break;
                        case 'admin':
                            roleConstant = await contract.methods.ADMIN_ROLE().call();
                            break;
                        case 'verifier':
                            roleConstant = await contract.methods.APPROVER_ROLE().call();
                            break;
                        case 'owner':
                            roleConstant = null; // No predefined constant for owner
                            break;
                        default:
                            throw new Error("Unknown role");
                    }

                    // If a role constant is defined, check the role
                    if (roleConstant) {
                        const hasRole = await contract.methods.hasRole(roleConstant, walletAddress).call();
                        if (hasRole) {
                            navigate(`/${role}`, { state: { walletAddress } });
                        } else {
                            toast.error("You do not have the required role to access this page.");
                            navigate('/');
                        }
                    } else {
                        // Handle navigation for owner role if needed
                        navigate('/owner', { state: { walletAddress } });
                    }
                } catch (error) {
                    console.error('Error during role check or navigation:', error);
                    toast.error("Check your role and try again.");
                    navigate('/');
                }
            } else {
                toast.error('MetaMask is not installed. Please install MetaMask and try again.');
                navigate('/');
            }
        };

        checkUserRole();

        if (walletAddress) {
            fetchEmployeeData();
        }
    }, [walletAddress, navigate]);

    const renderCell = (value) => {
        return value === undefined || value === null || value === '' ? 'NA' : value;
    };

    const getVerificationStatusStyle = (status) => {
        return {
            color: status ? 'green' : 'red',
            fontWeight: 'bold'
        };
    };

    function convertTimestampToDate(timestamp) {
        // Convert the timestamp to milliseconds
        const date = new Date(timestamp * 1000);

        // Format the date and time
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false  // Use 24-hour format; set to true for 12-hour format
        };

        return date.toLocaleString('en-US', options);
    }

    // Function to handle file download from IPFS
    const handleDownload = async (ipfsHash, fileName) => {
        try {
            const ipfsUrl = `${process.env.REACT_APP_PINATA_URL}${ipfsHash}`;
            const response = await fetch(ipfsUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch file from IPFS');
            }
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName; // Set the filename for the downloaded file
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file from IPFS:', error);
            toast.error('Error downloading file from IPFS.');
        }
    };

    const handleViewDocument = (documentUrl) => {
        setSelectedDocument(documentUrl);
    };

    const closeModal = () => {
        setSelectedDocument(null);
    };

    // Filter data by year, branch, and document type
    const filteredData = employeeDataList
        .filter(data => (selectedYear ? data.year === selectedYear : true))
        .filter(data => (selectedBranch ? data.branch === selectedBranch : true))
        .filter(data => (selectedDocumentType ? data.documentType === selectedDocumentType : true));

    // Generate unique values for year, branch, and document type for filtering
    const years = Array.from(new Set(employeeDataList.map(data => data.year)));
    // const branches = Array.from(new Set(employeeDataList.map(data => data.branch)));
    const documentTypes = Array.from(new Set(employeeDataList.map(data => data.documentType)));

    return (
        <div>
            {walletAddress && <p><h1 style={{ color: "#9b2620" }}>{employeename}</h1></p>}

            {/* Filters */}
            <div className="filters">
                <label>
                    Department:
                    <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear}>
                        <option value="">ALL Departments</option>
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </label>
                {/* <label>
                    Branch:
                    <select onChange={(e) => setSelectedBranch(e.target.value)} value={selectedBranch}>
                        <option value="">All Branches</option>
                        {branches.map(branch => (
                            <option key={branch} value={branch}>{branch}</option>
                        ))}
                    </select>
                </label> */}
                <label>
                    Document Type:
                    <select onChange={(e) => setSelectedDocumentType(e.target.value)} value={selectedDocumentType}>
                        <option value="">All Document Types</option>
                        {documentTypes.map(docType => (
                            <option key={docType} value={docType}>{docType}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="table-container">
                <h2>Employee Documents</h2>
                {loading ? (
                    <div className="loader"></div> // Display circular loader
                ) : (
                    filteredData.length > 0 && (
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Department</th>
                                    <th>ID Number</th>
                                    <th>Document ID</th>
                                    <th>Added At</th>
                                    <th>Document Type</th>
                                    <th>Document Hash</th>
                                    <th>Verification Status</th>
                                    <th>Verified By</th>
                                    <th>Verified At</th>
                                    <th>View Document</th>
                                    <th>Download</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((data, index) => (
                                    <tr key={index}>
                                        <td>{renderCell(data.name)}</td>
                                        <td>{renderCell(data.year)}</td>
                                        <td>{renderCell(data.idnumber)}</td>
                                        <td>{renderCell(data.employeeID?.toString())}</td>
                                        <td>{renderCell(convertTimestampToDate(Number(data.addedAt).toString()))}</td>
                                        <td>{renderCell(data.documentType)}</td>
                                        <td>{renderCell(data.documentHash)}</td>
                                        <td style={getVerificationStatusStyle(data.verificationStatus)}>
                                            {data.verificationStatus ? 'Verified' : 'Not Verified'}
                                        </td>
                                        <td>{renderCell(data.verifiedBy?.join(', '))}</td>
                                        <td>
                                            {data.approvedAt ? convertTimestampToDate(data.approvedAt.toString()) : 'NA'}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleViewDocument(data.document)}
                                                className="view-document-button"
                                            >
                                                View Document
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDownload(data.documentHash, `${employeename}-${data.documentType}-${data.employeeID}`)}
                                                className="download-button"
                                            >
                                                Download
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
            </div>

            <button onClick={() => navigate('/fileupload')}>Go to File Upload</button>
            <ToastContainer />

            {/* Modal for Viewing Document */}
            {selectedDocument && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={closeModal}>&times;</span>
                        <iframe
                            src={selectedDocument}
                            title="Document Viewer"
                            width="100%"
                            height="600px"
                        ></iframe>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employee;
